import { parseSectionValue } from '@/entities/brief';

/**
 * Gates every AI-written typed-field value before it reaches the form: the only write path
 * for AI suggestions into a typed field. For `select`/`checklist`, intersects the candidate's
 * `selected` array against `config.options`; an out-of-vocabulary entry is silently dropped
 * (never invented), and an empty intersection means no write at all (`undefined`). Every other
 * type reuses the shared Zod boundary (`parseSectionValue`) — malformed AI output is rejected
 * the same way malformed API data is.
 *
 * Returns `undefined` to mean "do not write" — distinct from `null`, which is a legitimate
 * cleared value a caller might otherwise want to write deliberately.
 */
export function coerceAiValue(
  type: string,
  config: Record<string, unknown>,
  candidate: unknown,
): unknown {
  if (type === 'select' || type === 'checklist') {
    if (!candidate || typeof candidate !== 'object') return undefined;
    const selected = (candidate as { selected?: unknown }).selected;
    if (!Array.isArray(selected)) return undefined;

    const options = Array.isArray(config.options) ? (config.options as string[]) : [];
    const inVocabulary = selected.filter(
      (entry): entry is string => typeof entry === 'string' && options.includes(entry),
    );
    if (inVocabulary.length === 0) return undefined;

    const clamped =
      type === 'select' && config.multiple === false ? inVocabulary.slice(0, 1) : inVocabulary;
    return { selected: clamped };
  }

  const parsed = parseSectionValue(type, candidate, config);
  return parsed.success ? parsed.data : undefined;
}
